import { MenuType, PortalServiceMenuItemDto } from '@nx-frontend/shared/types';
import { axiosClient } from './axios';

export interface GetMenuTreeProps {
  gatewayUrl: string;
  workspaceId: string;
  menuType: MenuType;
  depth: number;
  parentKey?: string | string[];
  type?: 'note' | 'folder';
  abortController?: AbortController;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  forceReload?: boolean;
  sessionId?: string;
}

/**
 * Size of each chunk of parentKey to fetch
 * Needed to avoid URL length limit
 */
const PARENT_KEY_CHUNK_SIZE = 50;

export async function getWebnotesMenuTree({
  gatewayUrl,
  workspaceId,
  depth,
  parentKey,
  type,
  abortController,
  orderBy,
  orderDirection,
}: GetMenuTreeProps): Promise<PortalServiceMenuItemDto[]> {
  try {
    if (!workspaceId) {
      throw new Error('Workspace id is required');
    }

    const parentKeyChunks: string[] = [];

    if (Array.isArray(parentKey)) {
      // Split parentKey into chunks to avoid URL length limit
      for (let i = 0; i < parentKey.length; i += PARENT_KEY_CHUNK_SIZE) {
        parentKeyChunks.push(encodeURIComponent(parentKey.slice(i, i + PARENT_KEY_CHUNK_SIZE).join(',')));
      }
    } else {
      // If parentKey is a string, just push it to the array
      parentKeyChunks.push(parentKey || '');
    }

    // Fetch menu items for each chunk of parentKey
    const promises: Promise<{ data: PortalServiceMenuItemDto[] }>[] = parentKeyChunks.map((chunk) => {
      return axiosClient.get<PortalServiceMenuItemDto[]>(`${gatewayUrl}/ft:notes/menu`, {
        params: {
          workspace: workspaceId,
          depth: depth,
          parentKey: chunk || undefined,
          type: type,
          ...(orderBy ? { orderBy } : {}),
          ...(orderDirection ? { orderDirection } : {}),
        },
        ...(abortController ? { signal: abortController.signal, withCredentials: true } : { withCredentials: true }),
      });
    });
    const responses = await Promise.all(promises);

    // Flatten the array of arrays into a single array
    return responses.flatMap((response) => response.data);
  } catch (err) {
    return [];
  }
}
